// Global Scentodore app store using React context.
//
// Persists only non-sensitive UX state (guest cart id, currency preference,
// last quiz result) to localStorage. Authentication tokens live in
// sessionStorage — see `./auth.js`.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getCart, upsertCart, fetchCurrencyRates } from "./api";

const STORE_KEY = "scentodore_state_v2";

// Map currency preference to Shopify country code for @inContext queries
const CURRENCY_TO_COUNTRY = {
  CAD: "CA",
  USD: "US",
  AUD: "AU",
  GBP: "GB",
  EUR: "FR",
};

const CURRENCY_SYMBOLS = {
  CAD: "CA$",
  USD: "US$",
  AUD: "AU$",
  GBP: "£",
  EUR: "€",
};

const defaultState = {
  guestId: null,
  currency: "CAD",  // Store default (Shopify store's primary currency)
  quizProfile: null,
};

const AppStoreContext = createContext(null);

function newGuestId() {
  return `g_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
}

export function AppStoreProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (raw) return { ...defaultState, ...JSON.parse(raw) };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[store] failed to read persisted state", err);
    }
    return { ...defaultState, guestId: newGuestId() };
  });

  const [cart, setCart] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [rates, setRates] = useState({ USD: 1 });
  const [symbols, setSymbols] = useState(CURRENCY_SYMBOLS);

  // Country derived from selected currency, used to pass to Shopify @inContext queries
  const country = CURRENCY_TO_COUNTRY[state.currency] || "CA";

  // Persist state
  useEffect(() => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[store] failed to persist state", err);
    }
  }, [state]);

  // Load cart + rates on mount / guest change
  useEffect(() => {
    if (!state.guestId) return;
    getCart(state.guestId).then(setCart).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[store] getCart failed", err?.response?.status);
      setCart(null);
    });
    fetchCurrencyRates()
      .then((r) => { setRates(r.rates); setSymbols(r.symbols); })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[store] fetchCurrencyRates failed", err?.response?.status);
      });
  }, [state.guestId]);

  // Cart operations — wrapped in useCallback so downstream memoized components
  // don't have to worry about identity churn on every render.
  const persistCart = useCallback(async (lines, freeSample) => {
    const nextFreeSample = freeSample !== undefined ? freeSample : (cart?.free_sample || null);
    const updated = await upsertCart({
      guest_id: state.guestId,
      lines,
      free_sample: nextFreeSample,
      currency: state.currency,
    });
    setCart(updated);
    return updated;
  }, [cart?.free_sample, state.guestId, state.currency]);

  const addToCart = useCallback(async (product_id, size_label, quantity = 1, engraving = null) => {
    const lines = [...(cart?.lines || [])];
    const idx = lines.findIndex(
      (l) => l.product_id === product_id && l.size_label === size_label && (l.engraving || "") === (engraving || "")
    );
    if (idx >= 0) lines[idx].quantity += quantity;
    else lines.push({ product_id, size_label, quantity, engraving });
    const c = await persistCart(lines);
    setCartOpen(true);
    return c;
  }, [cart?.lines, persistCart]);

  const updateLineQuantity = useCallback(async (index, quantity) => {
    const lines = [...(cart?.lines || [])];
    if (!lines[index]) return;
    if (quantity <= 0) lines.splice(index, 1);
    else lines[index].quantity = quantity;
    return persistCart(lines);
  }, [cart?.lines, persistCart]);

  const removeLine = useCallback((index) => updateLineQuantity(index, 0), [updateLineQuantity]);

  const setFreeSample = useCallback(
    (product_id) => persistCart(cart?.lines || [], product_id),
    [cart?.lines, persistCart],
  );

  const clearCart = useCallback(() => persistCart([], null), [persistCart]);

  const setCurrency = useCallback((code) => {
    setState((s) => ({ ...s, currency: code }));
  }, []);

  const setQuizProfile = useCallback((profile) => {
    setState((s) => ({ ...s, quizProfile: profile }));
  }, []);

  const format = useCallback((amount, currencyOverride) => {
    // With Shopify, prices come pre-converted in the selected currency via
    // @inContext(country:). We just need to render them with the right symbol.
    const code = currencyOverride || state.currency;
    const sym = CURRENCY_SYMBOLS[code] || symbols[code] || "$";
    const value = Number(amount) || 0;
    return `${sym}${value.toFixed(2)}`;
  }, [symbols, state.currency]);

  const value = useMemo(
    () => ({
      state,
      cart,
      cartOpen,
      setCartOpen,
      searchOpen,
      setSearchOpen,
      addToCart,
      updateLineQuantity,
      removeLine,
      setFreeSample,
      clearCart,
      setCurrency,
      setQuizProfile,
      format,
      rates,
      symbols,
      country,
    }),
    [
      state, cart, cartOpen, searchOpen, rates, symbols, country,
      addToCart, updateLineQuantity, removeLine, setFreeSample,
      clearCart, setCurrency, setQuizProfile, format,
    ],
  );

  return <AppStoreContext.Provider value={value}>{children}</AppStoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(AppStoreContext);
  if (!ctx) throw new Error("useStore must be used inside AppStoreProvider");
  return ctx;
}
